/**
 * Shared persistence for "live matches the player can reconnect into".
 *
 * Each game owns its own localStorage key (Snake has its own model; the
 * legacy Pong reconnect lives in `lib/pongReconnect.ts`). This file
 * covers the games whose flow mirrors each other — TD and Math — so
 * the floating reconnect card has a single shape to consume.
 *
 * Lifecycle:
 *   • joinXRoom() → savePending(...) with what's known at join (token,
 *     room, stake, wallet).
 *   • onMatchFound → markMatchFound(opponentWallet) enriches the blob
 *     so the card can render the opponent avatar.
 *   • onMatchEnd → clearPending(): match resolved.
 *   • room.leave(true) (consensual / "Back to lobby") → clearPending().
 *   • room.onLeave (non-consensual drop / refresh / browser close) does
 *     NOT clear — the blob lives until the 10-min hard TTL expires.
 *     That's the window in which the card stays on the dashboard.
 */

export type ReconnectGame = "td" | "math";

export interface PendingMatch {
  game: ReconnectGame;
  roomId: string;
  reconnectionToken: string;
  stake: string;
  walletAddress: string | null;
  /** Filled in by `markMatchFound`. Null while still in the queue. */
  opponentWallet: string | null;
  /** Wall-clock at first save (join). */
  savedAtMs: number;
  /** Wall-clock at the first `match_found` for this room. Null until then. */
  matchStartedAtMs: number | null;
}

const STORAGE_KEYS: Record<ReconnectGame, string> = {
  td: "hupper.td.reconnect.v1",
  math: "hupper.math.reconnect.v1",
};

/** Hard upper bound — even server-side grace + match length stays well under this. */
const TTL_MS = 10 * 60_000;

function isExpired(p: PendingMatch): boolean {
  return Date.now() - p.savedAtMs > TTL_MS;
}

export function savePending(p: PendingMatch): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEYS[p.game], JSON.stringify(p));
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** Patch the opponent wallet onto an existing record without resetting `savedAtMs`. */
export function markMatchFound(game: ReconnectGame, opponentWallet: string | null): void {
  const cur = loadPending(game);
  if (!cur) return;
  savePending({
    ...cur,
    opponentWallet,
    matchStartedAtMs: cur.matchStartedAtMs ?? Date.now(),
  });
}

export function loadPending(game: ReconnectGame): PendingMatch | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS[game]);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingMatch;
    if (!parsed?.roomId || !parsed?.reconnectionToken) return null;
    if (isExpired(parsed)) {
      clearPending(game);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPending(game: ReconnectGame): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEYS[game]);
  } catch { /* ignore */ }
}

/** Snapshot of every pending match across the games this lib covers. */
export function loadAllPending(): PendingMatch[] {
  const out: PendingMatch[] = [];
  for (const game of Object.keys(STORAGE_KEYS) as ReconnectGame[]) {
    const p = loadPending(game);
    if (p) out.push(p);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────
// Reconnect-intent flag — sessionStorage so a click on the card asks the
// game page (on mount) to immediately try the reconnect path without
// re-queueing. Mirrors lib/pongReconnect's pattern.
// ─────────────────────────────────────────────────────────────────────────

const INTENT_KEYS: Record<ReconnectGame, string> = {
  td: "hupper.td.reconnect-intent",
  math: "hupper.math.reconnect-intent",
};

export function setReconnectIntent(game: ReconnectGame): void {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(INTENT_KEYS[game], "1"); } catch { /* */ }
}

export function consumeReconnectIntent(game: ReconnectGame): boolean {
  if (typeof window === "undefined") return false;
  try {
    const v = window.sessionStorage.getItem(INTENT_KEYS[game]);
    if (v) window.sessionStorage.removeItem(INTENT_KEYS[game]);
    return v === "1";
  } catch {
    return false;
  }
}
